import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Trash2 } from 'lucide-react';
import { useModalDrag } from '../../hooks/useModalDrag';
import { useResourceForm } from '../useResourceForm';
import { useResourceActions } from '../../stores';
import { generateResourceId, getResourceById } from '../resourceStore';
import { ScopeToggle, ColorPicker, FieldsList } from './ResourceFormFields';
import type { ResourceDefinition } from '../../types';

export interface ResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceId?: string;
  defaultScope?: ResourceDefinition['scope'];
}

export function ResourceModal({ isOpen, onClose, resourceId, defaultScope }: ResourceModalProps) {
  const { t } = useTranslation();
  const actions = useResourceActions();
  const { modalRef, position, isDragging, handleMouseDown, handleDoubleClick } = useModalDrag();

  const [draftId, setDraftId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && !resourceId && !draftId) {
      const id = generateResourceId('');
      const now = new Date().toISOString();
      actions.addResource({
        id,
        name: '',
        category: '',
        scope: defaultScope ?? { type: 'global' },
        fields: [],
        color: '#6366f1',
        createdAt: now,
        updatedAt: now,
      });
      setDraftId(id);
    }
  }, [isOpen, resourceId, draftId, actions, defaultScope]);

  useEffect(() => {
    if (!isOpen) setDraftId(null);
  }, [isOpen]);

  const effectiveId = resourceId || draftId || undefined;
  const form = useResourceForm(effectiveId);

  const handleClose = useCallback(() => {
    if (draftId) {
      const storeResource = getResourceById(draftId);
      if (!storeResource?.name?.trim()) {
        actions.deleteResource(draftId);
      }
    }
    setDraftId(null);
    onClose();
  }, [draftId, actions, onClose]);

  const handleDelete = useCallback(() => {
    if (form.deleteResource()) {
      setDraftId(null);
      onClose();
    }
  }, [form, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { handleClose(); return; }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { handleClose(); return; }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;
  if (!effectiveId) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[10001]" onClick={handleClose} />
      <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 pointer-events-none">
        <div
          ref={modalRef}
          className={`bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col ${position ? '' : 'pointer-events-auto'}`}
          style={position ? { position: 'fixed', left: position.x, top: position.y, pointerEvents: 'auto' } : undefined}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-2 border-b border-gray-100 shrink-0"
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <span className="text-xs font-medium text-gray-500">
              {resourceId ? t('resource.editResource') : t('resource.newResource')}
            </span>
            <button onClick={handleClose} onMouseDown={e => e.stopPropagation()} className="text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Form content */}
          <div className="flex-1 overflow-y-auto px-4 py-3" style={{ minHeight: 0 }}>
            {/* Name */}
            <div className="mb-3">
              <label className="block text-[10px] font-medium text-gray-500 mb-1">{t('resource.name')}</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => form.setName(e.target.value)}
                className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-gray-900 bg-white"
                autoFocus
              />
            </div>

            {/* Category */}
            <div className="mb-3">
              <label className="block text-[10px] font-medium text-gray-500 mb-1">{t('resource.category')}</label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => form.setCategory(e.target.value)}
                className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-gray-900 bg-white"
              />
            </div>

            <ScopeToggle scopeType={form.scopeType} onChange={form.setScopeType} />
            <ColorPicker color={form.color} onChange={form.setColor} />
            <FieldsList
              fields={form.fields}
              onAddField={form.addField}
              onUpdateField={form.updateField}
              onRemoveField={form.removeField}
            />
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              {resourceId && (
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  {t('common.delete')}
                </button>
              )}
            </div>
            <span className="text-[10px] text-gray-400">ESC · Cmd+Enter</span>
          </div>
        </div>
      </div>
    </>
  );
}

